import { useMemo } from 'react'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type { LeaveTypeDto, MyEligibleLeaveTypesParams } from '@/utils/api/leave-service/models'

import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useMyEligibleLeaveTypes } from '@/utils/api/leave-service'

interface LeaveEntitlementRow {
  id?: number
  name?: string
  code?: string
  annualEntitlement?: number
  annualRemaining?: number
  totalRemaining?: number
  daysTakenToDate?: number
  carryoverRemaining?: number
  carryoverExpiryDate?: string
  borrowingAllowed?: boolean
  borrowableRemaining?: number
  noticePeriodDays?: number
  entitlementDepleted?: boolean
  daysUsedPercentage?: number
}

interface LeaveEntitlementsTabProps {
  onApplyLeave?: (leaveType: LeaveTypeDto) => void
}

const SKELETON_ROW_KEYS = ['skeleton-1', 'skeleton-2', 'skeleton-3']

export function LeaveEntitlementsTab({ onApplyLeave }: LeaveEntitlementsTabProps) {
  const leaveTypesParams = useMemo<MyEligibleLeaveTypesParams>(() => ({
    pageable: { page: 0, size: 200 },
  }), [])
  const leaveTypesQuery = useMyEligibleLeaveTypes(
    leaveTypesParams,
    { query: { staleTime: 60_000 } },
  )

  const leaveTypes = useMemo(() => leaveTypesQuery.data?.data?.content ?? [], [leaveTypesQuery.data])

  const entitlementRows = useMemo<LeaveEntitlementRow[]>(() => {
    return leaveTypes.map((leaveType) => {
      const annualEntitlement = leaveType.balance?.annualEntitlement ?? leaveType.entitlements?.[0]?.days ?? 0
      const annualRemaining = leaveType.balance?.annualRemaining ?? leaveType.remainingDays ?? 0
      const daysTakenToDate = leaveType.balance?.daysTakenToDate ?? Math.max(0, annualEntitlement - annualRemaining)
      const totalRemaining = leaveType.balance?.totalRemaining ?? leaveType.remainingDays ?? annualRemaining
      const carryoverRemaining = leaveType.balance?.carryoverRemaining ?? 0
      const borrowableRemaining = leaveType.balance?.borrowableRemaining
        ?? leaveType.carryoverPolicy?.maxBorrowDays
      const borrowingAllowed = leaveType.balance?.borrowingAllowed
        ?? leaveType.carryoverPolicy?.borrowingAllowed
        ?? false
      const daysUsedPercentage = annualEntitlement > 0 ? (daysTakenToDate / annualEntitlement) * 100 : 0

      return {
        id: leaveType.id,
        name: leaveType.name,
        code: leaveType.code,
        annualEntitlement,
        annualRemaining,
        totalRemaining,
        daysTakenToDate,
        carryoverRemaining,
        carryoverExpiryDate: leaveType.balance?.carryoverExpiryDate,
        borrowingAllowed,
        borrowableRemaining,
        noticePeriodDays: leaveType.noticePeriodDays,
        entitlementDepleted: leaveType.entitlementDepleted,
        daysUsedPercentage,
      }
    })
  }, [leaveTypes])

  const columns = useMemo<ColumnConfig<LeaveEntitlementRow>[]>(() => ([
    {
      key: 'name',
      header: 'Leave Type',
      cell: (_value, row) => (
        <div className="flex flex-col gap-1">
          <span className="font-medium">{row.name ?? row.code ?? '-'}</span>
          {row.code && row.code !== row.name && (
            <span className="text-xs text-muted-foreground">{row.code}</span>
          )}
        </div>
      ),
    },
    {
      key: 'annualEntitlement',
      header: 'Annual Entitlement',
      type: 'number',
      cell: value => `${value ?? 0} days`,
    },
    {
      key: 'daysTakenToDate',
      header: 'Taken To Date',
      type: 'number',
      cell: value => `${value ?? 0} days`,
    },
    {
      key: 'annualRemaining',
      header: 'Annual Remaining',
      type: 'number',
      cell: value => (
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {(value as number | undefined) ?? 0}
            {' '}
            days
          </span>
        </div>
      ),
    },
    {
      key: 'carryoverRemaining',
      header: 'Carryover',
      cell: (_value, row) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {row.carryoverRemaining ?? 0}
            {' '}
            days
          </span>
          {row.carryoverExpiryDate
            ? (
              <span className="text-xs text-muted-foreground">
                Expires
                {' '}
                {new Date(row.carryoverExpiryDate).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: '2-digit',
                })}
              </span>
            )
            : null}
        </div>
      ),
    },
    {
      key: 'totalRemaining',
      header: 'Total Remaining',
      type: 'number',
      cell: (_value, row) => (
        <div className="flex items-center gap-2 w-full">
          <span className="font-medium">
            {row.totalRemaining ?? 0}
            {' '}
            days
          </span>
        </div>
      ),
    },
    {
      key: 'borrowableRemaining',
      header: 'Borrowing',
      type: 'number',
      cell: (_value, row) => {
        if (!row.borrowingAllowed) {
          return 'Not allowed'
        }
        return `${row.borrowableRemaining ?? 0} days available`
      },
    },
    {
      key: 'daysUsedPercentage',
      header: 'Usage (%)',
      sortable: false,
      filterable: false,
      cell: (_value, row) => (
        <div className="flex items-center gap-2 w-full">
          <Progress value={row.daysUsedPercentage ?? 0} className="h-2 flex-1" />
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {Math.round(row.daysUsedPercentage ?? 0)}
            %
          </span>
        </div>
      ),
    },
    {
      key: 'noticePeriodDays',
      header: 'Notice Period',
      type: 'number',
      cell: value => value ? `${value ?? 0} days` : 'Immediate',
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      filterable: false,
      cell: (_value, row) => {
        const requestCapacity = (row.totalRemaining ?? 0) + (row.borrowingAllowed ? (row.borrowableRemaining ?? 0) : 0)
        const canApply = requestCapacity > 0
        const leaveType = leaveTypes.find(lt => lt.id === row.id)

        return (
          <Button
            type="button"
            size="sm"
            variant={canApply ? 'default' : 'ghost'}
            disabled={!canApply}
            onClick={() => {
              if (leaveType && onApplyLeave) {
                onApplyLeave(leaveType)
              }
            }}
          >
            Apply
          </Button>
        )
      },
    },
  ]), [leaveTypes, onApplyLeave])

  return (
    <div className="space-y-4">
      {leaveTypesQuery.isError && (
        <Alert variant="destructive">
          <AlertTitle>Unable to load entitlements</AlertTitle>
          <AlertDescription>
            {leaveTypesQuery.error?.message ?? 'Please try again.'}
          </AlertDescription>
        </Alert>
      )}

      {leaveTypesQuery.isPending && (
        <div className="space-y-4">
          {SKELETON_ROW_KEYS.map(key => (
            <div key={key} className="h-16 bg-muted animate-pulse rounded" />
          ))}
        </div>
      )}

      {!leaveTypesQuery.isPending && entitlementRows.length === 0 && (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground mb-4">
            No leave types available for your organization.
          </p>
        </div>
      )}

      {!leaveTypesQuery.isPending && entitlementRows.length > 0 && (
        <EnhancedDataTable
          data={entitlementRows}
          columns={columns}
          emptyStateMessage="No leave entitlements available."
          loading={leaveTypesQuery.isPending}
          enableColumnFilters={false}
          initialColumnVisibility={{ daysUsedPercentage: false }}
          enableExport={false}
        />
      )}
    </div>
  )
}
